import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import cx from 'classnames';
import _ from 'lodash';
import dateformat from 'dateformat';
import { toast } from "react-toastify";
import {
  Row,
  Col,
  Button,
  Image,
  Offcanvas,
  Dropdown
} from 'react-bootstrap';

import styles from './permissions.module.scss'

import InfiniteScroll from "components/InfiniteScroll";
import { SuccessToast } from "components/Toast";

import { fetchAllUsers, invalidateUsers } from 'actions/userManagement';
import {
  setStoryboardUserRole, fetchStoryboardUsers, addUsersToStoryboard, updateStoryboardUserAccess,
  removeUserFromStoryboard
} from 'actions/storyboard';
import { MIXPANEL_PAGE_VIEWED, MIXPANEL_PAGE_VIEWED_STORYBOARD_USER_MANAGEMENT } from "constants/mixpanelEvents";
import { createMixpanelEvent } from "actions/mixpanel";
import { getPageViewedEventPayload } from "services/mixpanelUtil";

export default function StoryboardPermissions() {
  const dispatch = useDispatch();

  let { storyboardId } = useParams();

  const [state, setState] = useState({
    collaborate: false,
    updateRole: null,
    removeUser: null
  })

  const storyboardName = useSelector(state => _.get(state, 'storyboards.storyboard.item.name', null));

  const storyboardType = useSelector(state => _.get(state, 'storyboards.storyboard.item.type', null));

  const users = useSelector(state => _.get(state, "storyboards.storyboard.users.items", []));

  const pagination = useSelector(state => _.get(state, 'storyboards.storyboard.users.pagination', {}));

  const loading = useSelector(state => _.get(state, 'storyboards.storyboard.users.loading', false));

  const hasMoreItems = _.get(pagination, "current") < _.get(pagination, "totalPages") - 1;

  useEffect(() => {
    var mixPanelPayload = getPageViewedEventPayload(MIXPANEL_PAGE_VIEWED_STORYBOARD_USER_MANAGEMENT, storyboardId, storyboardName, storyboardType)
    dispatch(createMixpanelEvent(MIXPANEL_PAGE_VIEWED, mixPanelPayload))
    handleSearchUsers(0);
  }, [])

  const loadMoreUsers = () => {
    const nextPage = pagination.current + 1;
    if (nextPage >= pagination.totalPages) {
      return;
    }

    handleSearchUsers(nextPage);
  };

  const handleSearchUsers = (page) => {
    var request = {
      page: page,
      limit: 20,
      sort: 'createdDate',
      order: 'desc'
    }

    dispatch(fetchStoryboardUsers(storyboardId, request));
  }

  const toggleCollaborateUser = () => {
    setState(state => {
      return {
        ...state,
        collaborate: !state.collaborate
      }
    })
  }

  const handleCollaborateUsers = () => {
    handleSearchUsers(0);
    toggleCollaborateUser();
  }

  const handleChangeUserRole = (user) => {
    setState(state => {
      return {
        ...state,
        updateRole: user
      }
    })
  }

  const handleRemoveUserRole = (user) => {
    setState(state => {
      return {
        ...state,
        removeUser: user
      }
    })
  }

  const cancelUserActions = () => {
    setState(state => {
      return {
        ...state,
        updateRole: null,
        removeUser: null
      }
    })
  }

  return (
    <section className={styles.content}>
      <header className={styles.header}>
        <div>
          <div className={styles.title}>Roles & Permissions</div>
          <div className={styles.description}>Tweak the access control in Your Storyboard</div>
        </div>
        <Button
        className={styles.addButton}
        variant="primary"
        type="submit"
        onClick={toggleCollaborateUser}
        data-track-event="Mixpanel" data-track-props="Add user"
        >
          <i className={cx(styles.icon, 'cleverstory-plus')} />
          Add User
        </Button>
      </header>

      <section className={styles.storyboardUserList}>
        <InfiniteScroll
          className={styles.scrollable}
          loading={loading}
          hasMore={hasMoreItems}
          loadMore={() => {
            loadMoreUsers();
          }}
        >
          {
            users.map((item) => {
              return (
                <StoryboardUserRow
                  key={item.id}
                  storyboardUser={item}
                  onRoleChange={handleChangeUserRole}
                  onDelete={handleRemoveUserRole}
                />
              );
            })
          }
        </InfiniteScroll>
      </section>

      <StoryboardCollaborationView
        show={state.collaborate}
        onSave={handleCollaborateUsers}
        onCancel={toggleCollaborateUser} />

      <UpdateStoryboardUserRole
        storyboardUser={state.updateRole}
        onCancel={cancelUserActions}
        users={users} />

      <DeleteStoryboardUser
        storyboardUser={state.removeUser}
        onCancel={cancelUserActions}
        users={users} />
    </section>
  )
}

const StoryboardUserRow = ({ storyboardUser, onRoleChange, onDelete }) => {

  const dispatch = useDispatch();

  const user = storyboardUser.user;
  const currentUser = useSelector((state) => _.get(state, "users.me"));
  const canEdit = currentUser.id !== user.id;
  const options = [
    { value: 'owner', label: 'Owner' },
    { value: 'editor', label: 'Editor' },
    { value: 'viewer', label: 'Viewer' }
  ]


  const handleUserRoleChanges = (selectedRole) => {
    var request = {
      role: selectedRole
    }

    dispatch(updateStoryboardUserAccess(storyboardUser.id, request, () => {
      toast(
        <SuccessToast
          heading={`Success`}
          description={`User ${user.email} updated successfully`}
        />,
        {
          type: toast.TYPE.DEFAULT,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
        }
      );
    }))
  }

  return (
    <Row className={styles.userRow}>
      <Col md="4">
        <div className={styles.userDetails}>
          <div className={styles.iconContainer}>
            <div
              className={cx(styles.contactAvatar, {
                [styles.hasIcon]: user.icon,
              })}
            >
              {!user.icon && renderInitials(user)}{" "}
            </div>
            {user.icon && (
              <Image
                className={styles.contactIcon}
                src={user.icon.thumbnail}
                roundedCircle
              />
            )}
          </div>
          <div className={styles.userDetailsBody}>
            <h2 className={styles.userName}>
              {user.firstName} {user.lastName}
            </h2>
            <label className={styles.email}>{user.email}</label>
          </div>
        </div>
      </Col>
      <Col md="2" className={styles.userColumn}>
        {
          !canEdit &&
          <div className={styles.rolesInfo}>
            {_.get(storyboardUser, "role", '')}
            <span>&#9662;</span>
          </div>
        }
        {canEdit && <Dropdown>
          <Dropdown.Toggle as={CustomUserRoleToggle} variant="Secondary" id="dropdown-basic">
            {_.get(storyboardUser, "role", '') || 'Select role'}
          </Dropdown.Toggle>

          <Dropdown.Menu className={styles.userRoleMenu} >
            {options.map(option => {
              return (
                <Dropdown.Item
                  key={option.value}
                  onClick={() => handleUserRoleChanges(option.value)}
                >
                  {option.label}
                </Dropdown.Item>
              )
            })}
          </Dropdown.Menu>
        </Dropdown>}
      </Col>
      <Col md="2" className={styles.userColumn}>
        <div className={styles.lastActiveDate}>
          <label className={styles.lastActiveLabel}>Last Active</label>
          <span className={styles.lastActiveDate}>
            on {dateformat(user.lastLoginDate, "dd mmm yyyy")}
          </span>
        </div>
      </Col>
      <Col md={{ span: 2, offset: 2 }} className={cx(styles.userColumn)}>
        {canEdit && (
          <Button
            variant="light"
            disabled={!canEdit}
            onClick={() => onDelete(storyboardUser)}
          >
            Remove
          </Button>
        )}
      </Col>
    </Row>
  );
}

const CustomUserRoleToggle = React.forwardRef(({ children, onClick }, ref) => (
  <button
    ref={ref}
    className={cx(styles.rolesInfoChange)}
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
  >
    {children}
    <i className={cx( "cleverstory-drop-down")} />
  </button>
));

const UpdateStoryboardUserRole = ({ storyboardUser, onCancel, users }) => {
  const dispatch = useDispatch();

  const roles = [
    { value: 'owner', label: 'Owner' },
    { value: 'editor', label: 'Editor' },
    { value: 'viewer', label: 'Viewer' }
  ]

  const [state, setState] = useState({
    selected: null,
    updating: false
  });

  useEffect(() => {
    if (storyboardUser) {

      setState(state => {
        return {
          ...state,
          selected: storyboardUser.role
        }
      })
    }
  }, [storyboardUser]);

  useEffect(() => {
    if (users && storyboardUser) {
      let currentUser = users.find(user => {
        return user.id === storyboardUser.id;
      })

      setState(state => {
        return {
          ...state,
          updating: currentUser.updating
        }
      })
    }
  }, [users])

  const handleRoleChange = (role) => {
    setState(state => {
      return {
        ...state,
        selected: role
      }
    })
  }

  const handleSaveChanges = () => {
    var request = {
      role: state.selected
    }

    dispatch(updateStoryboardUserAccess(storyboardUser.id, request, () => {
      onCancel();
    }))
  }

  if (!storyboardUser) {
    return null;
  }

  return (
    <Offcanvas show={storyboardUser !== null} width="30vw" className={styles.updateStoryboardUserRoleOffCanvas}>
      <div className={styles.updateStoryboardUserRoleContainer}>
        <header className={styles.collaborationHeader}>
          <h1 className={styles.title}>Update User role</h1>
          <span className={styles.summary}>
            Lorem ipsum dolor sit amet, consetetur sadipscing elitr.
          </span>
        </header>

        {roles.map(role => {
          return (
            <StoryboardRoleRow key={role.value}
              role={role}
              selected={state.selected}
              onChange={handleRoleChange} />
          )
        })}

        <footer className={styles.buttonContainer}>
          <Button className={styles.resetButton}
            type="reset"
            variant="light"
            onClick={onCancel}>
            Cancel
          </Button>
          <Button className={styles.saveButton}
            type="submit"
            disabled={state.updating}
            onClick={handleSaveChanges}>
            {state.updating ? 'Saving...' : 'Save Changes'}
          </Button>
        </footer>
      </div>
    </Offcanvas>
  )
}

function DeleteStoryboardUser({ onCancel, storyboardUser, users }) {
  const dispatch = useDispatch();

  const [state, setState] = useState({
    deleting: false
  });

  useEffect(() => {
    if (users && storyboardUser) {
      let currentUser = users.find(user => {
        return user.id === storyboardUser.id;
      })

      if (currentUser) {
        setState(state => {
          return {
            ...state,
            deleting: currentUser.deleting
          }
        })
      }
    }
  }, [users])

  const handleDeleteInvitationClick = () => {
    dispatch(removeUserFromStoryboard(storyboardUser.id, () => {
      toast(
        <SuccessToast
          heading={`Success`}
          description={`Removed User from storyboard`}
        />,
        {
          type: toast.TYPE.DEFAULT,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
        }
      );
      onCancel();

      setState(state => {
        return {
          ...state,
          deleting: false
        }
      })
    }));
  };

  if (!storyboardUser) {
    return null;
  }

  return (
    <Offcanvas
      className={styles.confirmationDialog}
      show={storyboardUser !== null}
      backdrop={true}
    >
      <h1 className={styles.heading}>Remove User from Storyboard</h1>
      <div className={styles.summary}>
        <span>for {storyboardUser.user.email}</span> <br />
        <span>Are you sure you want to remove this user?</span>
      </div>

      <footer className={styles.actionContainer}>
        <Button
          variant="primary"
          className={styles.actionButton}
          type="submit"
          disabled={state.deleting}
          onClick={handleDeleteInvitationClick}
        >
          {state.deleting ? "Removing..." : "Remove User"}
        </Button>

        <Button
          type="reset"
          variant="secondary"
          onClick={() => {
            onCancel();
          }}
        >
          Cancel
        </Button>
      </footer>
    </Offcanvas>
  );
}

function StoryboardRoleRow({ role, selected, onChange }) {
  const handleSelectRole = () => {
    onChange(role.value);
  };

  return (
    <section className={styles.storyboardRoleRow} onClick={handleSelectRole}>
      <aside className={styles.userRoleSelection}>
        <input
          className={styles.actionSelection}
          name="userRole"
          type="radio"
          checked={role.value === selected}
          onChange={handleSelectRole}
        />
        <span className={styles.checkmark}></span>
      </aside>
      <aside className={styles.storyboardRoleRowDetail}>
        <h1 className={styles.storyboardRoleName}>{role.label}</h1>
      </aside>
    </section>
  );
}

const StoryboardCollaborationView = ({ show, onSave, onCancel }) => {
  const dispatch = useDispatch();

  let { storyboardId } = useParams();

  const [state, setState] = useState({
    query: ''
  });

  const users = useSelector((state) => _.get(state, "userManagement.users.items", []));

  const loading = useSelector((state) => _.get(state, "userManagement.users.loading", false));

  const addingUsers = useSelector(state => _.get(state, 'storyboards.storyboard.addingUsers', false));

  const pagination = useSelector((state) => _.get(state, "userManagement.users.pagination"));

  const hasMoreItems = _.get(pagination, "current") < _.get(pagination, "totalPages") - 1;

  const { id } = useParams();

  useEffect(() => {
    if (show) {
      handleSearch(state.query)
    } else {
      dispatch(invalidateUsers());
    }
  }, [show])

  const handleSearch = React.useCallback(
    _.debounce((query) => {
      dispatch(fetchAllUsers(id, 0, query));
    }, 300),
    []
  );

  const loadMoreUsers = () => {
    const nextPage = pagination.current + 1;
    if (nextPage >= pagination.totalPages) {
      return;
    }

    dispatch(fetchAllUsers(id, nextPage, state.query));
  };

  const handleCollaborateUsers = () => {
    var request = users.filter(item => {
      return item.edited;
    }).map(item => {
      return {
        user: item.id,
        role: item.role
      }
    });

    dispatch(addUsersToStoryboard(storyboardId, request, (response) => {
      onSave();
    }));
  }

  return (
    <Offcanvas show={show} width="50vw" className={styles.storyboardCollaborartionOffCanvas}>
      <div className={styles.storyboardCollaborationView}>
        <div className={styles.storyboardCollaborationInner}>
          <header className={styles.collaborationHeader}>
            <h1 className={styles.title}>Add Users to Storyboard</h1>
            <span className={styles.summary}>
              Your chance to offer seats on this rocket ship
            </span>
          </header>

          {users.length === 0 && loading &&
            <div className={styles.loaderContainer}>
              <label>Loading Users...</label>
            </div>
          }

          {users.length !== 0 &&
            <InfiniteScroll
              className={styles.scrollable}
              loading={loading}
              hasMore={hasMoreItems}
              loadMore={() => {
                loadMoreUsers();
              }}
            >
              {
                users.filter(item => item.enabled).map((item) => {
                  return (
                    <StoryboardCollaborationRow
                      key={item.id}
                      user={item}
                    />
                  );
                })
              }
            </InfiniteScroll>
          }
        </div>
        <footer className={styles.buttonContainer}>
          <Button 
            className={styles.actionButton}
            type="reset"
            variant="secondary"
            onClick={onCancel}>
            Cancel
          </Button>
          <Button 
            type="submit"
            variant="primary"
            disabled={addingUsers}
            onClick={handleCollaborateUsers}>
            {addingUsers ? 'Saving...' : 'Save Changes'}
          </Button>
        </footer>
      </div>
    </Offcanvas>
  )
}

const StoryboardCollaborationRow = ({ user }) => {
  const dispatch = useDispatch();

  const currentUser = useSelector((state) => _.get(state, "users.me"));
  const canEdit = currentUser.id !== user.id;

  const options = [
    { value: 'owner', label: 'Owner' },
    { value: 'editor', label: 'Editor' },
    { value: 'viewer', label: 'Viewer' }
  ]

  const renderRole = () => {
    var role = options.find(option => {
      return option.value === user.role;
    })

    return role ? role.label : null;
  }

  const handleRoleChange = (role) => {
    dispatch(setStoryboardUserRole(user.id, role));
  }

  return (
    <Row className={styles.userRow}>
      <Col md="8">
        <div className={styles.userDetails}>
          <div className={styles.iconContainer}>
            <div
              className={cx(styles.contactAvatar, {
                [styles.hasIcon]: user.icon,
              })}
            >
              {!user.icon && renderInitials(user)}{" "}
            </div>
            {user.icon && (
              <Image
                className={styles.contactIcon}
                src={user.icon.thumbnail}
                roundedCircle
              />
            )}
          </div>
          <div className={styles.userDetailsBody}>
            <h2 className={styles.userName}>
              {user.firstName} {user.lastName}
            </h2>
            <label className={styles.email}>{user.email}</label>
          </div>
        </div>
      </Col>
      {canEdit &&
        <Col md="4" className={styles.roleSelectionColumn}>
          <div className={styles.storyboardRoles}>
            <Dropdown>
              <Dropdown.Toggle variant="Secondary" id="dropdown-basic">
                {renderRole() || 'Select role'}
              </Dropdown.Toggle>

              <Dropdown.Menu>
                {options.map(option => {
                  return (
                    <Dropdown.Item
                      key={option.value}
                      onClick={handleRoleChange.bind(this, option.value)}>
                      {option.label}
                    </Dropdown.Item>
                  )
                })}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </Col>}
    </Row>
  )
}

function renderInitials(user) {
  let initial = "";

  if (user && user.name) {
    initial = user.name.charAt(0);
  } else {
    initial = user.email.charAt(0);
  }

  return initial.toUpperCase();
}